/* eslint-disable no-mixed-spaces-and-tabs */
import {
	useGetEmailTemplateHtmlQuery,
	useGetEmailTemplatesQuery,
	useGetSOAEmailTemplateDataQuery,
	useGetParsedEmailTemplateQuery,
	useGetHandlebarsHtmlQuery,
} from '@/features/templates/templatesApi';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { setTemplate } from '@/features/templates/templatesSlice';
import { TEMPLATE_GROUPS } from '@/features/templates/helper';
import { parseHtmlAndConvertStyles } from '@/utils/emailHelper';

const parser = new DOMParser();

const EmailTemplateAutofill = ({ setLoadingTemplate = (bool) => {} }) => {
	const formik = useFormikContext();
	const { values, setFieldValue } = formik;
	const { deal, client, template, html, subject } = values;
	const dispatch = useDispatch();
	const resolvedClientId = client?.clientId ?? getDealClientId(deal);

	useEffect(() => {
		console.log('[EmailTemplateAutofill] HTML UPDATE', html);

		const doc = parser.parseFromString(html, 'text/html');
		setFieldValue('body', doc.body.innerText.trim());
	}, [html]);

	useEffect(() => {
		console.log('[EmailTemplateAutofill] FORMIK VALUES UPDATE', values);
	}, [values]);

	const emailData = useSelector((state) => state.dialog?.data?.email);
	const preloadTemplateId = useSelector((state) => state.templatesSlice?.template);

	const preloadData = preloadTemplateId ? { templateId: preloadTemplateId } : emailData;

	const { data: emailTemplates, ...emailTemplatesQuery } = useGetEmailTemplatesQuery(
		{ type: preloadData?.template },
		{ skip: !preloadData?.template && !preloadData?.templateId },
	);

	useEffect(() => {
		if (emailTemplates?.[0] && (preloadData?.template || preloadData?.templateId)) {
			const chosenTemplate = preloadData?.template
				? emailTemplates.find((t) => t.master)
				: emailTemplates.find((t) => t.id === preloadData?.templateId);
			setFieldValue('template', chosenTemplate);
			setFieldValue('name', chosenTemplate?.name);
			setFieldValue('type', chosenTemplate?.type);
			if (!chosenTemplate?.master && chosenTemplate?.sortBy !== TEMPLATE_GROUPS.shared) {
				setFieldValue('replaceExisting', true);
			}
			dispatch(setTemplate(null));
		}
	}, [emailTemplates, preloadData?.template, preloadData?.templateId]);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });

	const { getFeatureFlags } = useFeatureFlagContext();
	const marbleLedger = Boolean(getFeatureFlags('marbleSOA')?.active);

	const { data: dealRow } = useGetDealRowQuery(deal?.dealId, {
		skip: !deal?.dealId,
	});

	const htmlQuery = useGetEmailTemplateHtmlQuery(
		{ id: template?.id, path: template?.path },
		{ skip: !template?.id || !template?.path },
	);

	const { data: soaTemplate, ...soaTemplateQuery } = useGetSOAEmailTemplateDataQuery(
		{ dealId: dealRow?.dealId },
		{
			skip:
				template?.type !== 'statementOfAdvice' || !dealRow?.dealId || !userDetails?.userId,
		},
	);

	useEffect(
		() =>
			console.log('EmailTemplateAutofill', { template, client, deal, html: htmlQuery?.data }),
		[template, client, deal],
	);

	const templateQuery = useGetParsedEmailTemplateQuery(
		{
			template,
			clientId: resolvedClientId,
			dealId: deal?.dealId,
			isMarble: marbleLedger,
		},
		{ skip: !template?.type },
	);
	const templateData = !template?.type ? null : templateQuery?.data;

	useEffect(() => {
		console.log('useGetParsedEmailTemplateQuery', {
			templateData,
			templateQuery,
			template,
			client,
			deal,
			skip: !template?.type || !resolvedClientId || !deal?.dealId,
		});
	}, [templateData, templateQuery, template, client, deal]);

	///
	/// Removing auto adding subject to email, will reinstate once ability for user to create own subjects is implemented
	///
	// useEffect(() => {
	// 	if (!subject) {
	// 		if (template?.type === 'statementOfAdvice' && soaTemplate?.subject) {
	// 			setFieldValue('subject', soaTemplate?.subject);
	// 		} else if (
	// 			template?.type === 'scopeOfAdvice' &&
	// 			clientRow?.name &&
	// 			userDetails?.broker?.name
	// 		) {
	// 			setFieldValue(
	// 				'subject',
	// 				`${clientRow?.name} - ${userDetails?.broker?.name} Scope Of Advice`
	// 			);
	// 		} else if (template?.type === 'preRenewal' && clientRow?.name) {
	// 			setFieldValue('subject', `Important: Insurance Renewal for ${clientRow?.name}`);
	// 		}
	// 	}
	// }, [
	// 	template?.type,
	// 	soaTemplate?.subject,
	// 	userDetails?.broker?.name,
	// 	clientRow?.name,
	// 	client?.hubspotId,
	// 	subject,
	// ]);

	const handlebarsQuery = useGetHandlebarsHtmlQuery(
		{ templateData, html: htmlQuery?.data?.toString() },
		{ skip: Object.keys(templateData ?? {}).length === 0 || !htmlQuery.data },
	);

	useEffect(() => {
		if (!templateQuery.isUninitialized) {
			templateQuery.refetch();
		}
	}, [resolvedClientId, deal?.dealId, template?.id, templateQuery.isUninitialized]);

	useEffect(() => {
		if (!soaTemplateQuery.isUninitialized) {
			soaTemplateQuery.refetch();
		}
	}, [resolvedClientId, deal?.dealId, template?.id, soaTemplateQuery.isUninitialized]);

	useEffect(() => {
		if (!handlebarsQuery.isUninitialized) {
			handlebarsQuery.refetch();
		}
	}, [resolvedClientId, deal?.dealId, template?.id, handlebarsQuery.isUninitialized]);

	useEffect(() => {
		if (handlebarsQuery.isLoading || handlebarsQuery.isFetching) {
			setLoadingTemplate(true);
		} else {
			setLoadingTemplate(false);
		}
		const getHtmlData = async () => {
			const htmlValue = (handlebarsQuery.data ?? '').toString();
			let parsedValue = '';
			try {
				parsedValue = await parseHtmlAndConvertStyles(htmlValue);
			} catch (e) {
				console.error('Error parsing data', e);
			}

			if (!parsedValue) return;
			console.log('Parsed value', parsedValue);
			setFieldValue('html', parsedValue.html);
			setFieldValue('template', {
				...template,
				isHtmlTemplate: (htmlQuery?.data ?? '').includes('<style>'),
			});
			setFieldValue('headStyle', parsedValue.headContent);
		};
		if (handlebarsQuery.data) {
			getHtmlData();
		}
		if (handlebarsQuery.isError) {
			setFieldValue('html', '');
			setFieldValue('headStyle', '');
		}
	}, [
		handlebarsQuery.isLoading,
		handlebarsQuery.isFetching,
		handlebarsQuery.data,
		handlebarsQuery.isError,
	]);
};
export default EmailTemplateAutofill;
